import type { MouseEventHandler, ReactNode } from 'react'

interface ClickableCardProps {
  /** Interactive selection state — maps to aria-pressed. */
  selected?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
  /** Accessible name for the card when content alone is insufficient. */
  ariaLabel?: string
  /** Additional Tailwind classes for padding, layout, etc. */
  className?: string
  children: ReactNode
}

/**
 * Generic interactive card tile. Renders a semantic <button> with
 * aria-pressed state and token-based selection ring.
 *
 * Use when a full-bleed clickable surface is needed — e.g. selectable
 * layer blocks, filter tiles. The Button component cannot express this
 * layout (it is inline-flex and drops className).
 *
 * Selection style: primary border + primary-container fill (token-only).
 * Default style: surface-container-lowest + outline-variant border.
 *
 * DESIGN.md § 8c — NEVER hand-roll clickable card styling in-page.
 */
export function ClickableCard({
  selected = false,
  onClick,
  ariaLabel,
  className = '',
  children,
}: ClickableCardProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={ariaLabel}
      onClick={onClick}
      className={[
        'w-full text-left rounded-xl border transition-all outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary/30',
        selected
          ? 'bg-primary-container border-primary shadow-md'
          : 'bg-surface-container-lowest border-outline-variant hover:border-primary hover:shadow-sm',
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </button>
  )
}
